import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Button, Tooltip, Chip, Paper} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { myApi } from '../../config/axiosConfig';
import useMediaQuery from '@mui/material/useMediaQuery';
import Swal from 'sweetalert2';
import MiTabla from '../../components/plantilla/tabla/MiTabla';
import { green, red } from '@mui/material/colors';
import BtnAcciones from '../../components/plantilla/tabla/BtnAcciones';

// Iconos
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import CrearEditarModal from './modales/CrearEditarPrecio';

const Precios = () => {

    const isNonMobile = useMediaQuery("(min-width:600px)");

    const [data, setData] = useState([]);
    const [selected, setSelected] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);


    const handleOpenModal = (precio = null) => {
        setSelected(precio);
        setIsEditing(!!precio);
        setModalOpen(true);
    };

    const handleDelete = useCallback(async (id) => {
        try {
            Swal.fire({
                title: '¿Estás seguro de eliminar el precio?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: 'green',
                cancelButtonColor: 'red',
            }).then(async (result) => {
                if (result.isConfirmed) {
                    let { data } = await myApi.delete(`/cargasFuel/deletePrecio/${id}`);

                    if (data.success) {
                        Swal.fire({
                            icon: 'success',
                            title: 'Precio eliminado',
                        });

                        setData(prevData => prevData.filter(precio => precio.id !== id));
                    } else {
                        Swal.fire({
                            icon: 'error',
                            title: 'Error al eliminar el precio',
                        });
                    }
                }
            });
            
        } catch (error) {
            console.error('Error deleting price:', error);
        }
    }, []);

    const handleEdit = useCallback(async (priceRow) => {
        handleOpenModal(data.find(precio => precio.id === priceRow.id));
        setIsEditing(true);
        setSelected(priceRow);
        
    }, []);

    const columns = [
        {
            field: 'fecha', headerName: 'FECHA',
            flex: 1,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => formatDate(params.value).substring(0, 8),
        },
        { field: 'precio', headerName: 'PRECIO',
            flex: 1,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => {
                return params.value != null
                ? `$${params.value.toLocaleString('en-US', {
                    minimumFractionDigits: 4,
                    maximumFractionDigits: 4,
                    })}`
                : '';
            }
        },
        { field: 'moneda', headerName: 'MONEDA',
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "fuente",
            headerName: "ESTABLECIMIENTO",
            flex: 1,
            headerAlign: "center",
            align: "center",
            valueFormatter: (params) => {
                if (
                    params.value === "DEF_COLOMBIA"
                ) {
                    return "COLOMBIA";
                }

                if (
                    params.value === "DEF_ARGUINDEGUI"
                ) {
                    return "ARGUINDEGUI";
                }

                return params.value || "";
            }
        },
        {
            field: "tipo",
            headerName: "PRODUCTO",
            flex: 1,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => {
                const tipo = String(params.value ?? "");

                if (tipo === "1") {
                    return "CLEAR DIESEL";
                }

                if (tipo === "2") {
                    return "RED DIESEL";
                }

                if (tipo === "3") {
                    return "DEF";
                }

                return tipo;
            }
        },
        {
            field: 'actions',
            headerName: 'ACCIONES',
            type: 'actions',
            width: 150,
            getActions: (params) => [
                <BtnAcciones
                    icon={<BorderColorIcon />}
                    label="Editar"
                    onClick={() => handleEdit(params.row)}
                />,
                <BtnAcciones
                    icon={<DeleteIcon />}
                    label="Eliminar"
                    onClick={() => handleDelete(params.row.id)}
                />
            ],
        }
    ];

    const fetch = async () => 
    {
        try 
        {
            const response = await myApi.get('/cargasFuel/getPrices');
            if(response.data.success)
                setData(response.data.data);
            else
                setData([]);
        } 
        catch(error) 
        {
            console.error('Error fetching prices:', error);
        }
    };

    const handleRowClick = (params) => {

    }

    useEffect(() => {
        fetch();
    }, []);

    //Función para formatear la fecha
    const formatDate = (date) => {
        const options = {
            year: '2-digit', // Año con dos dígitos
            month: '2-digit', // Mes con dos dígitos
            day: '2-digit', // Día con dos dígitos
            hour: '2-digit', // Hora con dos dígitos
            minute: '2-digit', // Minutos con dos dígitos
            second: '2-digit',    // Segundos con dos dígitos
            hour12: false, // Formato de 24 horas
        };
    
        const formattedDate = new Date(date).toLocaleString('en-US', options);
    
        // Formato deseado: MM/DD/YY HH:mm
        const [datePart, timePart] = formattedDate.split(', '); // Separar fecha y hora
        const [month, day, year] = datePart.split('/'); // Separar mes, día y año
        //const formattedDateTime = `${month}/${day}/${year} ${timePart.slice(0, 5)}`; // Construir el formato
        const formattedDateTime = `${month}/${day}/${year} ${timePart}`; // Construir el formato
    
        return formattedDateTime;
    };

    return (
        <Paper sx={{padding: 2}}>
            <Grid container>
                <Grid size={{ xs: 12, md: 8 }}>
                    <Typography variant="h4" fontWeight="bold" marginBottom={2}>
                        Precios
                    </Typography>
                </Grid>
                <Grid item size={{ xs: 12, md: 4 }}
                    sx={{
                        display: 'flex',
                        justifyContent: isNonMobile ? 'flex-end' : 'start'
                    }}>
                    <Button
                        variant="contained"
                        startIcon={<AddCircleIcon />}
                        onClick={() => handleOpenModal()}
                        sx={{ backgroundColor: 'black', marginBottom: 1 }}
                    >
                        Agregar
                    </Button>
                </Grid>
                {/** {LISTA DE PRECIOS} */}
                <Grid size={12}
                    marginTop={1}>
                    <MiTabla
                        columns={columns}
                        data={data}
                        rowClick={handleRowClick}
                    />
                </Grid>
                {/* {MODAL EDITAR/CREAR} */}
                <CrearEditarModal
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    refresh={fetch}
                    precio={selected}
                    isEditing={isEditing}
                />
            </Grid>
        </Paper>

    )

}

export default Precios